import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Megaphone, ImageIcon, BookOpen, Loader2, Download, Copy, Check,
  Target, DollarSign, Lightbulb, TrendingUp, ListChecks, Users,
  Globe, Upload, X as XIcon, Sparkles, AlertCircle, Star,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MarketingStudio = () => {
  const [niches, setNiches] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [usage, setUsage] = useState(null);

  // Ad creative state
  const [adForm, setAdForm] = useState({
    niche: '', platform: '', business_name: '', ad_brief: '', cta_goal: 'Get Started',
    website_url: '',
  });
  const [logoPreview, setLogoPreview] = useState(null);   // data URL for preview
  const [logoBase64, setLogoBase64] = useState(null);     // raw base64 sent to API
  const [adLoading, setAdLoading] = useState(false);
  const [adResult, setAdResult] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);

  // Playbook state
  const [pbForm, setPbForm] = useState({
    niche: '', business_name: '', monthly_budget: 2000, goals: '',
    offer: '', website_url: '',
  });
  const [pbLoading, setPbLoading] = useState(false);
  const [pbResult, setPbResult] = useState(null);

  useEffect(() => {
    bootstrap();
  }, []);

  const bootstrap = async () => {
    try {
      const [nichesRes, platformsRes, usageRes] = await Promise.all([
        axios.get(`${API_URL}/marketing/niches`),
        axios.get(`${API_URL}/marketing/ad-platforms`),
        axios.get(`${API_URL}/marketing/usage`),
      ]);
      setNiches(nichesRes.data.niches || []);
      setPlatforms(platformsRes.data.platforms || []);
      setUsage(usageRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fmtLimit = (n) => (n === -1 ? 'Unlimited' : n === 0 ? 'Not included' : n);

  const generateAd = async () => {
    if (!adForm.niche || !adForm.platform || !adForm.business_name.trim() || !adForm.ad_brief.trim()) {
      toast.error('Please fill niche, platform, business name, and describe your ad');
      return;
    }
    setAdLoading(true);
    setAdResult(null);
    try {
      const payload = { ...adForm };
      if (logoBase64) payload.logo_base64 = logoBase64;
      const { data } = await axios.post(`${API_URL}/marketing/ad-creative`, payload, { timeout: 180000 });
      setAdResult(data);
      toast.success(
        data.website_audited ? 'Ad generated (website context applied)' : 'Ad creative generated'
      );
      bootstrap();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setAdLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      toast.error('Please upload a PNG, JPG, WEBP, or SVG image');
      return;
    }
    if (file.size > 1_500_000) {
      toast.error('Logo must be under 1.5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = typeof dataUrl === 'string' ? dataUrl.split(',')[1] : null;
      setLogoBase64(b64);
      setLogoPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    setLogoBase64(null);
    setLogoPreview(null);
  };

  const generatePlaybook = async () => {
    if (!pbForm.niche || !pbForm.business_name.trim() || !pbForm.goals.trim() || !pbForm.monthly_budget) {
      toast.error('Please fill in all playbook fields');
      return;
    }
    setPbLoading(true);
    setPbResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/marketing/playbook`, pbForm, { timeout: 120000 });
      setPbResult(data);
      toast.success('Marketing playbook generated');
      bootstrap();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setPbLoading(false);
    }
  };

  const copyText = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1800);
    } catch {
      toast.error('Copy failed');
    }
  };

  const downloadImage = () => {
    if (!adResult?.image_url) return;
    const a = document.createElement('a');
    a.href = adResult.image_url;
    a.download = `${adResult.business_name.replace(/\s+/g, '-').toLowerCase()}-${adResult.platform}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <Header
          title="Marketing Studio"
          subtitle="Generate ad creatives and niche-specific marketing playbooks powered by AI"
        />

        {/* Quota summary */}
        {usage && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="marketing-usage-summary">
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Ad Creatives</p>
                <p className="text-lg font-semibold text-slate-900 mt-1">
                  {usage.ad_creatives.used} / {fmtLimit(usage.ad_creatives.limit)}
                </p>
              </div>
              <ImageIcon className="w-8 h-8 text-indigo-500" />
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Playbooks</p>
                <p className="text-lg font-semibold text-slate-900 mt-1">
                  {usage.playbooks.used} / {fmtLimit(usage.playbooks.limit)}
                </p>
              </div>
              <BookOpen className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
        )}

        <Tabs defaultValue="creative" className="space-y-6" data-testid="marketing-tabs">
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="creative" data-testid="tab-creative">Ad Creative</TabsTrigger>
            <TabsTrigger value="playbook" data-testid="tab-playbook">Marketing Playbook</TabsTrigger>
          </TabsList>

          {/* ==================== AD CREATIVE TAB ==================== */}
          <TabsContent value="creative" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Form */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">Design your ad</h2>

                <div>
                  <Label>Niche</Label>
                  <Select value={adForm.niche} onValueChange={(v) => setAdForm({ ...adForm, niche: v })}>
                    <SelectTrigger data-testid="ad-niche-select"><SelectValue placeholder="Select niche" /></SelectTrigger>
                    <SelectContent>
                      {niches.map((n) => (<SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Platform & Size</Label>
                  <Select value={adForm.platform} onValueChange={(v) => setAdForm({ ...adForm, platform: v })}>
                    <SelectTrigger data-testid="ad-platform-select"><SelectValue placeholder="Select platform" /></SelectTrigger>
                    <SelectContent>
                      {platforms.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label} ({p.size})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Business Name</Label>
                  <Input
                    value={adForm.business_name}
                    onChange={(e) => setAdForm({ ...adForm, business_name: e.target.value })}
                    placeholder="Acme Dental Studio"
                    data-testid="ad-business-name-input"
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                    Describe your ad <span className="text-indigo-600 font-semibold">(AI follows this exactly)</span>
                  </Label>
                  <Textarea
                    value={adForm.ad_brief}
                    onChange={(e) => setAdForm({ ...adForm, ad_brief: e.target.value })}
                    placeholder="e.g. A bold vertical story ad showing a happy customer holding our product, with '30% OFF' in big red text on top-left. Warm sunset colors, minimalist layout, leave space for a CTA button at bottom."
                    rows={5}
                    data-testid="ad-brief-input"
                    className="resize-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Be specific — mention colors, layout, elements, mood, text overlays. The AI will follow your brief closely.
                  </p>
                </div>

                <div>
                  <Label className="flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-emerald-600" /> Website URL <span className="text-slate-400">(optional — we'll audit it)</span>
                  </Label>
                  <Input
                    value={adForm.website_url}
                    onChange={(e) => setAdForm({ ...adForm, website_url: e.target.value })}
                    placeholder="https://yourbusiness.com"
                    data-testid="ad-website-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    We quickly scan your site for products, tone, and positioning.
                  </p>
                </div>

                <div>
                  <Label className="flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5 text-purple-600" /> Brand Logo <span className="text-slate-400">(optional)</span>
                  </Label>
                  {logoPreview ? (
                    <div className="mt-2 flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                      <img src={logoPreview} alt="Logo" className="w-14 h-14 object-contain bg-white rounded border border-slate-200" />
                      <div className="flex-1 text-sm text-slate-700">Logo uploaded — will be incorporated in the ad</div>
                      <button onClick={clearLogo} className="text-slate-400 hover:text-rose-600" data-testid="ad-logo-clear-btn" aria-label="Remove logo">
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="logo-upload"
                      className="mt-1 flex flex-col items-center justify-center gap-1 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors"
                      data-testid="ad-logo-upload-label"
                    >
                      <Upload className="w-5 h-5 text-slate-400" />
                      <span className="text-sm text-slate-600">Click to upload logo</span>
                      <span className="text-xs text-slate-400">PNG / JPG / WEBP / SVG · max 1.5 MB</span>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        className="hidden"
                        onChange={handleLogoUpload}
                        data-testid="ad-logo-upload-input"
                      />
                    </label>
                  )}
                </div>

                <div>
                  <Label>CTA Goal</Label>
                  <Input
                    value={adForm.cta_goal}
                    onChange={(e) => setAdForm({ ...adForm, cta_goal: e.target.value })}
                    placeholder="Book Free Consult"
                    data-testid="ad-cta-input"
                  />
                </div>

                <Button
                  onClick={generateAd}
                  disabled={adLoading}
                  className="w-full btn-primary"
                  data-testid="ad-generate-btn"
                >
                  {adLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating ad (may take ~30s)...</>
                  ) : (
                    <>Generate Ad Creative</>
                  )}
                </Button>
              </div>

              {/* Result */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                {!adResult && !adLoading && (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400 py-16" data-testid="ad-empty-state">
                    <ImageIcon className="w-12 h-12 mb-3" />
                    <p className="text-sm">Your generated ad will appear here</p>
                  </div>
                )}
                {adLoading && (
                  <div className="flex flex-col items-center justify-center h-full py-16">
                    <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-3" />
                    <p className="text-sm text-slate-500">
                      {adForm.website_url ? 'Auditing your website, designing image, writing copy…' : 'Designing your image and writing copy…'}
                    </p>
                  </div>
                )}
                {adResult && (
                  <div className="space-y-4" data-testid="ad-result">
                    {(adResult.website_audited || adResult.logo_used) && (
                      <div className="flex flex-wrap gap-2" data-testid="ad-result-badges">
                        {adResult.website_audited && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium border border-emerald-200">
                            <Globe className="w-3 h-3" /> Website audited
                          </span>
                        )}
                        {adResult.logo_used && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-medium border border-purple-200">
                            <Upload className="w-3 h-3" /> Logo incorporated
                          </span>
                        )}
                      </div>
                    )}
                    {adResult.image_url && (
                      <div>
                        <img
                          src={adResult.image_url}
                          alt="Generated ad"
                          className="w-full rounded-lg border border-slate-200"
                        />
                        <Button variant="outline" size="sm" className="mt-2" onClick={downloadImage} data-testid="ad-download-btn">
                          <Download className="w-4 h-4 mr-2" /> Download Image
                        </Button>
                      </div>
                    )}
                    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                      <p className="text-xs uppercase text-indigo-600 font-semibold tracking-wide mb-1">Primary Copy</p>
                      <p className="font-semibold text-slate-900">{adResult.copy.headline}</p>
                      <p className="text-sm text-slate-700 mt-1">{adResult.copy.body}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="inline-flex items-center px-3 py-1 bg-white rounded-full text-xs font-medium text-indigo-700 border border-indigo-200">
                          CTA: {adResult.copy.cta}
                        </span>
                        <button
                          onClick={() => copyText(`${adResult.copy.headline}\n\n${adResult.copy.body}\n\nCTA: ${adResult.copy.cta}`, 'primary')}
                          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          data-testid="ad-copy-primary-btn"
                        >
                          {copiedIdx === 'primary' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {copiedIdx === 'primary' ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>

                    {adResult.copy.variants?.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-slate-700">A/B Test Variants</p>
                        {adResult.copy.variants.map((v, i) => (
                          <div key={v.id || v.headline} className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`ad-variant-${i}`}>
                            <p className="font-medium text-slate-900 text-sm">{v.headline}</p>
                            <p className="text-sm text-slate-600 mt-1">{v.body}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-slate-500">CTA: {v.cta}</span>
                              <button
                                onClick={() => copyText(`${v.headline}\n\n${v.body}\n\nCTA: ${v.cta}`, i)}
                                className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                              >
                                {copiedIdx === i ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                {copiedIdx === i ? 'Copied' : 'Copy'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ==================== PLAYBOOK TAB ==================== */}
          <TabsContent value="playbook" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4 lg:col-span-1 h-fit">
                <h2 className="text-lg font-semibold text-slate-900">Build your playbook</h2>

                <div>
                  <Label>Niche</Label>
                  <Select value={pbForm.niche} onValueChange={(v) => setPbForm({ ...pbForm, niche: v })}>
                    <SelectTrigger data-testid="pb-niche-select"><SelectValue placeholder="Select niche" /></SelectTrigger>
                    <SelectContent>
                      {niches.map((n) => (<SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Business Name</Label>
                  <Input
                    value={pbForm.business_name}
                    onChange={(e) => setPbForm({ ...pbForm, business_name: e.target.value })}
                    placeholder="Apex Roofing Co"
                    data-testid="pb-business-name-input"
                  />
                </div>

                <div>
                  <Label>Monthly Budget (USD)</Label>
                  <Input
                    type="number"
                    min="100"
                    value={pbForm.monthly_budget}
                    onChange={(e) => setPbForm({ ...pbForm, monthly_budget: parseFloat(e.target.value) || 0 })}
                    data-testid="pb-budget-input"
                  />
                </div>

                <div>
                  <Label>Primary Goals</Label>
                  <Textarea
                    value={pbForm.goals}
                    onChange={(e) => setPbForm({ ...pbForm, goals: e.target.value })}
                    placeholder="Generate 30 qualified leads per month in Dallas TX"
                    rows={3}
                    data-testid="pb-goals-input"
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                    What are you offering? <span className="text-slate-400">(optional)</span>
                  </Label>
                  <Textarea
                    value={pbForm.offer}
                    onChange={(e) => setPbForm({ ...pbForm, offer: e.target.value })}
                    placeholder="Residential roof repair and full replacement with 25-year warranty. Free inspections and insurance claim assistance."
                    rows={3}
                    data-testid="pb-offer-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    The more specific, the more tailored your plan will be.
                  </p>
                </div>

                <div>
                  <Label className="flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-emerald-600" /> Website URL <span className="text-slate-400">(optional — we'll audit it)</span>
                  </Label>
                  <Input
                    value={pbForm.website_url}
                    onChange={(e) => setPbForm({ ...pbForm, website_url: e.target.value })}
                    placeholder="https://yourbusiness.com"
                    data-testid="pb-website-input"
                  />
                </div>

                <Button
                  onClick={generatePlaybook}
                  disabled={pbLoading}
                  className="w-full btn-primary"
                  data-testid="pb-generate-btn"
                >
                  {pbLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building playbook...</>
                  ) : (
                    <>Generate Playbook</>
                  )}
                </Button>
              </div>

              {/* Result */}
              <div className="lg:col-span-2 space-y-4">
                {!pbResult && !pbLoading && (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400" data-testid="pb-empty-state">
                    <BookOpen className="w-12 h-12 mx-auto mb-3" />
                    <p className="text-sm">Your comprehensive marketing playbook will appear here</p>
                  </div>
                )}
                {pbLoading && (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Synthesizing niche best-practices with your inputs…</p>
                  </div>
                )}
                {pbResult && (
                  <div className="space-y-4" data-testid="pb-result">
                    {pbResult.website_audited && (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium border border-emerald-200">
                          <Globe className="w-3 h-3" /> Website audited — plan tailored to your site
                        </span>
                      </div>
                    )}

                    {/* Honest Assessment */}
                    {pbResult.personalized.honest_assessment && (
                      <Card title="Honest Assessment" icon={<AlertCircle className="w-5 h-5 text-amber-600" />}>
                        {pbResult.personalized.honest_assessment.feasibility_score !== undefined && (
                          <div className="mb-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-700">Feasibility Score</span>
                              <span className={`text-lg font-bold ${
                                pbResult.personalized.honest_assessment.feasibility_score >= 7 ? 'text-emerald-600'
                                : pbResult.personalized.honest_assessment.feasibility_score >= 5 ? 'text-amber-600'
                                : 'text-rose-600'
                              }`}>
                                {pbResult.personalized.honest_assessment.feasibility_score}/10
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all ${
                                  pbResult.personalized.honest_assessment.feasibility_score >= 7 ? 'bg-emerald-500'
                                  : pbResult.personalized.honest_assessment.feasibility_score >= 5 ? 'bg-amber-500'
                                  : 'bg-rose-500'
                                }`}
                                style={{ width: `${(pbResult.personalized.honest_assessment.feasibility_score || 0) * 10}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {pbResult.personalized.honest_assessment.reality_check && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
                            <p className="text-sm text-slate-800"><span className="font-semibold">Reality check: </span>{pbResult.personalized.honest_assessment.reality_check}</p>
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          {pbResult.personalized.honest_assessment.strengths?.length > 0 && (
                            <div>
                              <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-1">Strengths</p>
                              <ul className="space-y-1">
                                {pbResult.personalized.honest_assessment.strengths.map((s, i) => (
                                  <li key={s} className="text-sm text-slate-700">+ {s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {pbResult.personalized.honest_assessment.weaknesses?.length > 0 && (
                            <div>
                              <p className="text-xs uppercase tracking-wide text-rose-700 font-semibold mb-1">Weaknesses</p>
                              <ul className="space-y-1">
                                {pbResult.personalized.honest_assessment.weaknesses.map((w, i) => (
                                  <li key={w} className="text-sm text-slate-700">- {w}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        {pbResult.personalized.honest_assessment.estimations && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                            <Stat label="Conservative" value={`${pbResult.personalized.honest_assessment.estimations.conservative_leads_per_month || 0}/mo`} />
                            <Stat label="Realistic" value={`${pbResult.personalized.honest_assessment.estimations.realistic_leads_per_month || 0}/mo`} />
                            <Stat label="Best Case" value={`${pbResult.personalized.honest_assessment.estimations.best_case_leads_per_month || 0}/mo`} />
                            <Stat label="Break-even" value={pbResult.personalized.honest_assessment.estimations.break_even_month || '—'} />
                          </div>
                        )}
                      </Card>
                    )}

                    {/* Recommended Method */}
                    {pbResult.personalized.recommended_method && (
                      <Card title="Recommended Method for You" icon={<Star className="w-5 h-5 text-yellow-500" />}>
                        <div className="mb-3">
                          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Best channel for your situation</p>
                          <p className="text-xl font-bold text-indigo-700" data-testid="pb-recommended-channel">
                            {pbResult.personalized.recommended_method.channel}
                          </p>
                        </div>
                        {pbResult.personalized.recommended_method.why_this_one && (
                          <p className="text-sm text-slate-700 mb-3 italic">"{pbResult.personalized.recommended_method.why_this_one}"</p>
                        )}
                        {(pbResult.personalized.recommended_method.first_90_days || pbResult.personalized.recommended_method.first_30_days)?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">First 90 Days</p>
                            <ol className="space-y-1 list-decimal list-inside">
                              {(pbResult.personalized.recommended_method.first_90_days || pbResult.personalized.recommended_method.first_30_days).map((a, i) => (
                                <li key={a} className="text-sm text-slate-700">{a}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        {pbResult.personalized.recommended_method.monthly_budget_split && (
                          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                            <p className="text-xs uppercase tracking-wide text-indigo-700 font-semibold mb-1">Budget Split</p>
                            <p className="text-sm text-slate-800">{pbResult.personalized.recommended_method.monthly_budget_split}</p>
                          </div>
                        )}
                      </Card>
                    )}

                    {/* Best Platforms */}
                    <Card title="Best Ad Platforms for Your Niche" icon={<Target className="w-5 h-5 text-indigo-600" />}>
                      <ul className="space-y-2">
                        {pbResult.static.best_platforms.map((p, i) => (
                          <li key={p.id || p.name} className="flex gap-2">
                            <span className="font-semibold text-slate-900 shrink-0">{i + 1}. {p.name}:</span>
                            <span className="text-sm text-slate-600">{p.why}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>

                    {/* Budget Benchmarks */}
                    <Card title="Budget Benchmarks" icon={<DollarSign className="w-5 h-5 text-emerald-600" />}>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <Stat label="Monthly Low" value={`$${pbResult.static.budget_benchmarks.monthly_low.toLocaleString()}`} />
                        <Stat label="Monthly High" value={`$${pbResult.static.budget_benchmarks.monthly_high.toLocaleString()}`} />
                        <Stat label="Cost per Lead" value={pbResult.static.budget_benchmarks.cost_per_lead} />
                        <Stat label="Cost per Click" value={pbResult.static.budget_benchmarks.cost_per_click} />
                      </div>
                      <p className="text-xs text-slate-500 mt-3">{pbResult.static.budget_benchmarks.notes}</p>
                    </Card>

                    {/* Personalized Budget Allocation */}
                    {pbResult.personalized.budget_allocation?.length > 0 && (
                      <Card title={`Your $${pbResult.monthly_budget.toLocaleString()} Budget Allocation`} icon={<TrendingUp className="w-5 h-5 text-purple-600" />}>
                        <div className="space-y-2">
                          {pbResult.personalized.budget_allocation.map((a, i) => (
                            <div key={a.id || a.channel} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex-1">
                                <p className="font-medium text-slate-900">{a.channel}</p>
                                <p className="text-xs text-slate-600 mt-1">{a.rationale}</p>
                              </div>
                              <div className="text-right shrink-0 ml-3">
                                <p className="font-semibold text-indigo-700">${a.amount?.toLocaleString()}</p>
                                <p className="text-xs text-slate-500">{a.percent}%</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Week by Week */}
                    {pbResult.personalized.week_by_week?.length > 0 && (
                      <Card title="90-Day Plan (Week by Week)" icon={<ListChecks className="w-5 h-5 text-blue-600" />}>
                        <div className="space-y-3">
                          {pbResult.personalized.week_by_week.map((w, i) => (
                            <div key={w.id || w.week} className="border-l-4 border-indigo-200 pl-3">
                              <p className="font-semibold text-slate-900">Week {w.week}: {w.focus}</p>
                              <ul className="mt-1 space-y-1">
                                {(w.actions || []).map((a, j) => (
                                  <li key={`${w.id || w.week}-${a}`} className="text-sm text-slate-600">• {a}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Campaign Structure */}
                    <Card title="Campaign Structure Template" icon={<ListChecks className="w-5 h-5 text-orange-600" />}>
                      <ol className="space-y-2 list-decimal list-inside">
                        {pbResult.static.campaign_structure.map((s, i) => (
                          <li key={s} className="text-sm text-slate-700">{s}</li>
                        ))}
                      </ol>
                    </Card>

                    {/* Targeting Presets */}
                    <Card title="Targeting Presets" icon={<Users className="w-5 h-5 text-rose-600" />}>
                      <div className="space-y-2 text-sm">
                        {Object.entries(pbResult.static.targeting_presets).map(([k, v]) => (
                          <div key={k}>
                            <span className="font-medium text-slate-900 capitalize">{k.replace(/_/g, ' ')}: </span>
                            <span className="text-slate-600">{v}</span>
                          </div>
                        ))}
                      </div>
                    </Card>

                    {/* Copy Examples */}
                    <Card title="Ad Copy Examples for Your Niche" icon={<Copy className="w-5 h-5 text-teal-600" />}>
                      <ul className="space-y-2">
                        {pbResult.static.copy_examples.map((c, i) => (
                          <li key={c} className="text-sm text-slate-700 p-2 bg-slate-50 rounded border border-slate-200">
                            "{c}"
                          </li>
                        ))}
                      </ul>
                    </Card>

                    {/* KPIs */}
                    <Card title="KPIs to Track" icon={<TrendingUp className="w-5 h-5 text-violet-600" />}>
                      <div className="flex flex-wrap gap-2">
                        {pbResult.static.kpis.map((k, i) => (
                          <span key={k} className="px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-xs font-medium">
                            {k}
                          </span>
                        ))}
                      </div>
                    </Card>

                    {/* Quick Wins */}
                    {pbResult.personalized.quick_wins?.length > 0 && (
                      <Card title="Quick Wins (Low-Cost)" icon={<Lightbulb className="w-5 h-5 text-amber-600" />}>
                        <ul className="space-y-1">
                          {pbResult.personalized.quick_wins.map((q, i) => (
                            <li key={q} className="text-sm text-slate-700">✓ {q}</li>
                          ))}
                        </ul>
                      </Card>
                    )}

                    {/* Expected Outcomes */}
                    {pbResult.personalized.expected_outcomes && (
                      <Card title="Expected Outcomes" icon={<TrendingUp className="w-5 h-5 text-emerald-600" />}>
                        <p className="text-sm text-slate-700">{pbResult.personalized.expected_outcomes}</p>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

const Card = ({ title, icon, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="font-semibold text-slate-900">{title}</h3>
    </div>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="p-2 bg-slate-50 rounded border border-slate-200">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="font-semibold text-slate-900 text-sm mt-0.5">{value}</p>
  </div>
);

export default MarketingStudio;
